import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ZeosApiContext } from '../../../context/ZeosApiContext';
import { createLogger } from '../../../services/Logger';
import {
  IIncentiveContract,
  IIncentiveLookupResponse,
} from '../../../services/zeos-api/contracts/Incentive';

type SummaryFormat = "pdf" | "docx";

interface IProps {
  selectedPropertyId: number | null;
  incentives: IIncentiveContract[];
  isSuperAdmin: boolean;
  onRefresh: () => void;
}

interface IResourceLink {
  label: string;
  href?: string;
  kind: "download-summary" | "navigate" | "placeholder";
  path?: string;
}

const resourceLinks: IResourceLink[] = [
  // { label: "Upload Proposal", kind: "placeholder", href: "#" },
  { label: "Download Summary", kind: "download-summary" },
  { label: "Send to Partner", kind: "placeholder", href: "#" },
  { label: "Manage Profile", kind: "navigate", path: "/profile" },
  // { label: "Contact ReWire Advisor", kind: "placeholder", href: "#" },
  { label: "View Roadmap", kind: "placeholder", href: "#" },
  // { label: "Data Miner", kind: "navigate", path: "/incentives/data-miner" },
];

const statusBannerClass = (variant: "success" | "error") =>
  variant === "error"
    ? "text-red-800 bg-red-50 border-red-200"
    : "text-green-800 bg-green-50 border-green-200";

const resourceActionClass =
  "w-full text-left rounded-md px-3 py-2.5 text-sm font-medium text-zeos-orange-dk transition-colors hover:bg-orange-50 hover:text-zeos-orange-md focus:outline-none focus-visible:ring-2 focus-visible:ring-zeos-orange-lt disabled:cursor-not-allowed disabled:text-zeos-grey-md disabled:hover:bg-transparent";

export const IncentivesResources = ({
  selectedPropertyId,
  incentives,
  isSuperAdmin,
  onRefresh,
}: IProps) => {
  const api = useContext(ZeosApiContext);
  const log = createLogger("IncentivesResources");
  const navigate = useNavigate();
  const [summaryFormat, setSummaryFormat] = useState<SummaryFormat>("pdf");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<"success" | "error">(
    "success"
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [manualSearch, setManualSearch] = useState("");
  const [manualSearchResults, setManualSearchResults] = useState<IIncentiveLookupResponse[]>([]);
  const [selectedManualIncentive, setSelectedManualIncentive] =
    useState<IIncentiveLookupResponse | null>(null);
  const [manualReason, setManualReason] = useState("");
  const [manualSearchLoading, setManualSearchLoading] = useState(false);
  const [manualStatus, setManualStatus] = useState<string | null>(null);
  const [manualStatusVariant, setManualStatusVariant] = useState<"success" | "error">(
    "success"
  );

  const handleSummaryDownload = async (event: React.MouseEvent) => {
    event.preventDefault();
    if (!selectedPropertyId) {
      setStatusVariant("error");
      setStatusMessage("Select a property before downloading.");
      return;
    }

    try {
      setIsDownloading(true);
      setStatusMessage("Preparing download...");
      const response = await api.Incentive.downloadSummaryByPropertyId(
        selectedPropertyId,
        summaryFormat
      );
      if (response.zeosStatus !== "success" || !response.rawResponse) {
        throw new Error("Download failed.");
      }

      const contentType =
        (response.rawResponse.headers["content-type"] as string) || "application/octet-stream";
      const blob = new Blob([response.rawResponse.data], {
        type: contentType,
      });
      const fileName = resolveFileName(
        response.rawResponse.headers["content-disposition"],
        `IncentiveSummary.${summaryFormat}`
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setStatusVariant("success");
      setStatusMessage("Summary downloaded!");
    } catch (error: any) {
      log.error("Failed to download summary", error);
      setStatusVariant("error");
      setStatusMessage("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const manualLinks = incentives.filter(
    (item) => item.isManualLink && item.incentiveId > 0
  );

  useEffect(() => {
    if (!isSuperAdmin) {
      return undefined;
    }

    let isMounted = true;
    const timer = window.setTimeout(async () => {
      try {
        setManualSearchLoading(true);
        const response = await api.Incentive.searchIncentivesAsync(manualSearch, 20);
        if (!isMounted) return;
        if (response.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(response.message || "Unable to search incentives.");
        }
        setManualSearchResults(response.data ?? []);
      } catch (error: any) {
        if (isMounted) {
          setManualStatusVariant("error");
          setManualStatus(error?.message || "Unable to search incentives.");
        }
      } finally {
        if (isMounted) {
          setManualSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [api.Incentive, isSuperAdmin, manualSearch]);

  const handleManualAdd = async () => {
    if (!selectedPropertyId) {
      setManualStatusVariant("error");
      setManualStatus("Select a property before linking.");
      return;
    }

    if (!selectedManualIncentive || selectedManualIncentive.incentiveId <= 0) {
      setManualStatusVariant("error");
      setManualStatus("Choose an incentive before linking.");
      return;
    }

    try {
      const response = await api.Incentive.addManualLinkAsync(
        selectedPropertyId,
        selectedManualIncentive.incentiveId,
        manualReason.trim() || null
      );
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Failed to add manual link.");
      }
      setManualStatusVariant("success");
      setManualStatus("Manual link saved.");
      setManualSearch("");
      setSelectedManualIncentive(null);
      setManualReason("");
      onRefresh();
    } catch (error: any) {
      log.error("Failed to add manual incentive link", error);
      setManualStatusVariant("error");
      setManualStatus(error?.message || "Manual link failed.");
    }
  };

  const handleManualRemove = async (incentiveId: number) => {
    if (!selectedPropertyId) {
      setManualStatusVariant("error");
      setManualStatus("Select a property before unlinking.");
      return;
    }

    try {
      const response = await api.Incentive.removeManualLinkAsync(
        selectedPropertyId,
        incentiveId
      );
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Failed to remove manual link.");
      }
      setManualStatusVariant("success");
      setManualStatus("Manual link removed.");
      onRefresh();
    } catch (error: any) {
      log.error("Failed to remove manual incentive link", error);
      setManualStatusVariant("error");
      setManualStatus(error?.message || "Manual unlink failed.");
    }
  };

  const renderResourceItem = (link: IResourceLink) => {
    if (link.kind === "download-summary") {
      return (
        <li key={link.label} className="relative">
          <button
            type="button"
            onClick={handleSummaryDownload}
            className={`${resourceActionClass} flex items-center justify-between gap-2 pr-[5.5rem]`}
            disabled={isDownloading}
          >
            <span className="min-w-0 truncate">
              {isDownloading ? "Downloading..." : link.label}
            </span>
          </button>
          <div
            className="absolute right-3 top-1/2 inline-flex -translate-y-1/2 rounded-md border border-gray-200 bg-white p-0.5"
            role="group"
            aria-label="Summary format"
            onClick={(event) => event.stopPropagation()}
          >
            {(["pdf", "docx"] as SummaryFormat[]).map((format) => {
              const isActive = summaryFormat === format;
              return (
                <button
                  key={format}
                  type="button"
                  onClick={() => setSummaryFormat(format)}
                  className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                    isActive
                      ? "bg-zeos-orange-dk text-white"
                      : "text-zeos-grey-dk hover:bg-orange-50 hover:text-zeos-orange-dk"
                  }`}
                  aria-pressed={isActive}
                >
                  {format}
                </button>
              );
            })}
          </div>
        </li>
      );
    }

    if (link.kind === "navigate" && link.path) {
      return (
        <li key={link.label}>
          <button
            type="button"
            onClick={() => navigate(link.path!)}
            className={resourceActionClass}
          >
            {link.label}
          </button>
        </li>
      );
    }

    return (
      <li key={link.label}>
        <a href={link.href || "#"} className={`${resourceActionClass} block no-underline`}>
          {link.label}
        </a>
      </li>
    );
  };

  return (
    <div className="flex w-full flex-col gap-4 text-sm">
      <section className="overflow-hidden border border-gray-200 bg-white shadow-sm">
        <header className="border-b border-gray-100 px-4 pb-3 pt-4">
          <h2 className="text-base font-semibold text-zeos-black">Resources</h2>
          <p className="text-xs text-zeos-grey-dk mb-0">
            Actions and tools for this property&apos;s incentive set.
          </p>
        </header>

        <ul className="m-0 list-none space-y-0.5 p-2">{resourceLinks.map(renderResourceItem)}</ul>

        {statusMessage && (
          <div
            className={`mx-3 mb-3 rounded-md border px-3 py-2 text-xs ${statusBannerClass(
              statusVariant
            )}`}
            role="status"
          >
            {statusMessage}
          </div>
        )}

        {isSuperAdmin && (
          <div className="border-t border-gray-100 bg-zeos-grey-lt/40 px-4 py-4">
            <div className="mb-1 flex items-center gap-2">
              <span className="inline-block h-4 w-1 rounded-full bg-zeos-blue" aria-hidden="true" />
              <h3 className="text-xs font-semibold uppercase tracking-wide text-zeos-black">
                Manual Incentive Links
              </h3>
              <span className="rounded bg-zeos-blue-lt px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zeos-blue">
                Admin
              </span>
            </div>
            <p className="mb-3 text-xs text-zeos-grey-dk">
              Search by incentive or program name. IDs are hidden because they are only database internals.
            </p>

            <label className="mb-2 block">
              <span className="sr-only">Search incentives</span>
              <input
                type="search"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs text-zeos-black placeholder:text-zeos-grey-md focus:border-zeos-orange-md focus:outline-none focus:ring-2 focus:ring-zeos-orange-lt"
                placeholder="Search incentives..."
                value={manualSearch}
                onChange={(event) => {
                  setManualSearch(event.target.value);
                  setSelectedManualIncentive(null);
                }}
              />
            </label>

            <div className="mb-3 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white">
              {manualSearchLoading ? (
                <p className="p-3 text-xs text-zeos-grey-md">Searching...</p>
              ) : manualSearchResults.length === 0 ? (
                <p className="p-3 text-xs text-zeos-grey-md">No incentives found.</p>
              ) : (
                manualSearchResults.map((item) => {
                  const isSelected =
                    selectedManualIncentive?.incentiveId === item.incentiveId;
                  return (
                    <button
                      key={item.incentiveId}
                      type="button"
                      className={`block w-full border-b border-gray-100 px-3 py-2.5 text-left text-xs last:border-b-0 transition-colors ${
                        isSelected
                          ? "border-l-4 border-l-zeos-orange-dk bg-orange-50 text-zeos-orange-dk"
                          : "border-l-4 border-l-transparent text-zeos-black hover:bg-orange-50/60"
                      }`}
                      onClick={() => {
                        setSelectedManualIncentive(item);
                        setManualSearch(item.name);
                      }}
                    >
                      <span className="block font-semibold">{item.name}</span>
                      <span className="mt-0.5 block text-zeos-grey-dk">
                        {item.programName}
                        {item.stateCode ? ` · ${item.stateCode}` : ""}
                        {item.programStatus ? ` · ${item.programStatus}` : ""}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-semibold text-zeos-black">Reason (optional)</span>
              <textarea
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs text-zeos-black placeholder:text-zeos-grey-md focus:border-zeos-orange-md focus:outline-none focus:ring-2 focus:ring-zeos-orange-lt"
                placeholder="Why is this incentive being linked?"
                rows={2}
                maxLength={1000}
                value={manualReason}
                onChange={(event) => setManualReason(event.target.value)}
              />
            </label>

            <button
              type="button"
              onClick={handleManualAdd}
              disabled={!selectedManualIncentive}
              className="mb-3 rounded-md bg-zeos-orange-dk px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-zeos-orange-md disabled:cursor-not-allowed disabled:opacity-50"
            >
              Link selected incentive
            </button>

            {manualLinks.length === 0 ? (
              <p className="text-xs text-zeos-grey-md">No manual links.</p>
            ) : (
              <ul className="m-0 list-none space-y-2 p-0">
                {manualLinks.map((item) => (
                  <li
                    key={`manual-${item.incentiveId}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-zeos-black"
                  >
                    <span className="min-w-0 truncate font-medium">{item.name}</span>
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-zeos-red hover:underline"
                      onClick={() => handleManualRemove(item.incentiveId)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {manualStatus && (
              <div
                className={`mt-3 rounded-md border px-3 py-2 text-xs ${statusBannerClass(
                  manualStatusVariant
                )}`}
                role="status"
              >
                {manualStatus}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

const resolveFileName = (
  contentDisposition: string | undefined,
  fallback: string
): string => {
  if (!contentDisposition) {
    return fallback;
  }
  const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
  return match?.[1] || fallback;
};
